import { useMutation, useQueryClient } from 'react-query'
import request from '../request'
import { type Notification } from '@demandlocal/utils/lib/mongo/models/Notifications/Notifications'
import dayjs from 'dayjs'
import { type UserNotification } from '@demandlocal/utils/lib/mongo/models/Notifications/UserNotifications'

// Mutation hook with optimistic update
export const usePostReadAllNotifications = () => {
  const queryClient = useQueryClient()

  return useMutation(
    // Mutation function: Mark a notification as read
    () =>
      request({
        url: `/notifications/read-all`,
        method: 'POST',
      }),
    {
      // onMutate runs before the mutation function and allows us to update the cache optimistically
      onMutate: async () => {
        // Cancel outgoing refetches for both queries to prevent overwriting our optimistic updates
        await queryClient.cancelQueries(['userNotifications'])
        await queryClient.cancelQueries(['notificationsUnreadCount'])

        // Snapshot the previous state for both queries so we can roll back on error
        const previousNotifications = queryClient.getQueryData(['userNotifications'])
        const previousUnreadCount = queryClient.getQueryData(['notificationsUnreadCount'])

        // Optimistically update the notifications list
        queryClient.setQueryData(['userNotifications'], (oldData: any) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              data: page.data.map(
                (notification: Notification & Pick<UserNotification, 'readAt'>) => {
                  // If this is the notification being marked as read, update it
                  if (!notification.readAt) {
                    return { ...notification, readAt: dayjs().toDate() }
                  }
                  return notification
                },
              ),
            })),
          }
        })

        // Optimistically update the unread count by subtracting 1 (but ensure it doesn't go below 0)
        queryClient.setQueryData(['notificationsUnreadCount'], (oldData: any) => {
          if (!oldData || typeof oldData.count !== 'number') return oldData
          return { count: Math.max(oldData.count - 1, 0) }
        })

        // Return the snapshots so they can be used to roll back if needed
        return { previousNotifications, previousUnreadCount }
      },
      // onError rolls back the cache to its previous state if the mutation fails
      onError: (err, variables, context: any) => {
        if (context?.previousNotifications) {
          queryClient.setQueryData(['userNotifications'], context.previousNotifications)
        }
        if (context?.previousUnreadCount) {
          queryClient.setQueryData(
            ['notificationsUnreadCount'],
            context.previousUnreadCount,
          )
        }
      },
      // onSettled refetches the notifications regardless of success or failure,
      // ensuring that the cache remains in sync with the server
      onSettled: () => {
        queryClient.invalidateQueries(['userNotifications'])
      },
    },
  )
}
